<script setup lang="ts">
import cloneDeep from 'lodash-es/cloneDeep'
import { CloseIcon } from 'mdi-vue3'
import { computed, ref, unref } from 'vue'
import { useI18n } from 'vue-i18n'

import ADivider from '../../ADivider.vue'
import AButton from '../AButton/AButton.vue'
import { ARadioInput, type RadioInputOption } from '../generics'
import { type TableFilterOperator, type TableFilterOptions, type TableFilterValueOptions } from '.'
import TableFilterDate from './TableFilterDate.vue'
import TableFilterSelect from './TableFilterSelect.vue'
import TableFilterText from './TableFilterText.vue'

const properties = defineProps<{
  filter: TableFilterOptions
}>()

const emit = defineEmits<{
  (event: 'reset'): void
  (event: 'close'): void
}>()

const { t } = useI18n({
  messages: {
    en: {
      apply: 'Apply',
      reset: 'Remove',
    },
    nl: {
      apply: 'Toepassen',
      reset: 'Verwijderen',
    },
  },
})

const { t: translateOperator } = useI18n<{ message: Record<TableFilterOperator, string> }, 'en' | 'nl'>({
  useScope: 'global',
  messages: {
    en: {
      equal: 'Equal to',
      less: 'Less than',
      less_than_or_equal_to: 'Less than or equal to',
      greater: 'Greater than',
      greater_than_or_equal_to: 'Greater than or equal to',
      range: 'Range',
    },
    nl: {
      equal: 'Gelijk aan',
      less: 'Kleiner dan',
      less_than_or_equal_to: 'Kleiner dan of gelijk aan',
      greater: 'Groter dan',
      greater_than_or_equal_to: 'Groter dan of gelijk aan',
      range: 'Bereik',
    },
  },
})

const defaultValue: TableFilterValueOptions = {
  operator: properties.filter.operator || 'equal',
  values: [],
}
const operatorOrder: TableFilterOperator[] = ['less', 'less_than_or_equal_to', 'equal', 'greater', 'greater_than_or_equal_to', 'range']

const model = defineModel<TableFilterValueOptions | undefined>()
const initialModel = unref(model)
const editing = ref(initialModel ? cloneDeep(initialModel) : defaultValue)

const operatorOptions = computed((): RadioInputOption[] =>
  (properties.filter.operators ?? ['equal'])
    .map((operator) => ({ label: translateOperator(operator), value: operator }))
    .sort((a, b) => operatorOrder.indexOf(a.value) - operatorOrder.indexOf(b.value)),
)

function onApply() {
  model.value = editing.value
  emit('close')
}

function onReset() {
  editing.value.values = []
  editing.value.operator = properties.filter.operator
  emit('reset')
}

function onClose() {
  emit('close')
}
</script>
<template>
  <div class="atabix-table-filter-section">
    <h1 class="flex items-center justify-between text-lg font-bold">
      <span>{{ filter.label }}</span>
      <AButton :icon="CloseIcon" size="sm" variant="outline" @click="onClose()"></AButton>
    </h1>
  </div>
  <div v-if="operatorOptions.length > 1" class="atabix-table-filter-section">
    <ARadioInput v-model="editing.operator" :items="operatorOptions"></ARadioInput>
  </div>
  <TableFilterSelect v-if="filter.type === 'select'" v-model="editing" :filter="filter" />
  <TableFilterText v-if="filter.type === 'text' || filter.type === 'number' || filter.type === undefined" v-model="editing" :filter="filter" />
  <TableFilterDate v-if="filter.type === 'date'" v-model="editing" :filter="filter" />
  <ADivider />
  <div class="atabix-table-filter-section__buttons">
    <AButton :disabled="editing.values.length === 0" variant="outline" @click="onReset()">
      {{ t('reset') }}
    </AButton>
    <AButton :disabled="!editing.values[0]" @click="onApply()">
      {{ t('apply') }}
    </AButton>
  </div>
</template>

<style lang="scss">
.atabix-table-filter-section {
  @apply max-h-[40vh];
  @apply overflow-y-auto;
  @apply p-4;
  & + & {
    @apply border-t;
  }
}

.atabix-table-filter-section__buttons {
  @apply p-4;
  @apply flex flex-row gap-2 justify-between;
}
.atabix-table-filter-search {
  @apply flex;
  @apply items-center;
  @apply h-[40px];
  @apply my-0;
  @apply py-0;
  @apply relative;
  @apply select-none;
}

.atabix-table-filter-search-icon {
  @apply h-5;
  @apply text-form-foreground-val-placeholder;
  @apply absolute;
  @apply left-0;
  @apply px-2;
}

.atabix-table-filter-search-input {
  @apply flex;
  @apply h-10;
  @apply w-full;
  @apply rounded-md;
  @apply border;
  @apply border-border;
  @apply bg-background;
  @apply px-8;
  @apply leading-5;
  @apply py-2;
  @apply text-base;
  @apply ring-offset-background;
  @apply file:border-0;
  @apply file:bg-transparent;
  @apply file:text-base;
  @apply file:font-medium;
  @apply placeholder:text-foreground-placeholder;
  @apply focus-visible:outline-none;
  @apply focus-visible:ring-2;
  @apply focus-visible:ring-background-selected;
  @apply focus-visible:ring-offset-2;
  @apply disabled:cursor-not-allowed;
  @apply disabled:bg-background-disabled;

  &::placeholder {
    @apply text-form-foreground-val-placeholder;
  }
}

.atabix-table-filter-search-shortcut {
  @apply absolute;
  @apply pr-2;
  @apply right-0;
  @apply text-base;
  @apply text-form-foreground-helptext;
  @apply tracking-widest;
}
</style>
